import { Component, inject, viewChild, computed, signal } from '@angular/core';
import { Dialog, DialogRef, DIALOG_DATA } from '@angular/cdk/dialog';
import { MibButtonComponent } from '@medinbox/angular';
import { GuestDisclaimerForm, GuestFormData } from './guest-disclaimer-form/guest-disclaimer-form';
import { MibLogo } from '../mib-header/mib-logo';
import { TermsConditionsDialog } from '../copyright-and-legal-link/terms-conditions';

export interface DisclaimerData {
  showGuestForm?: boolean;
  formData?: GuestFormData;
}

@Component({
  selector: 'mai-disclaimer',
  imports: [MibLogo, MibButtonComponent, GuestDisclaimerForm],
  templateUrl: './disclaimer.html',
})
export class Disclaimer {
  readonly dialogRef = inject(DialogRef<GuestFormData | boolean>);
  readonly data = inject<DisclaimerData>(DIALOG_DATA, { optional: true });
  private readonly dialog = inject(Dialog);

  showGuestForm = signal(this.data?.showGuestForm ?? false);

  // Access the guest form component
  guestFormComponent = viewChild(GuestDisclaimerForm);

  // Computed signal to check if the form is valid
  isFormValid = computed(() => {
    const form = this.guestFormComponent()?.guestForm;
    if (!form) return false;

    // Check if all fields are valid
    return form.email().valid() && form.fullName().valid();
  });

  onGuestFormSubmitted(formData: GuestFormData): void {
    // Guest form submission - return the form data to the caller
    this.dialogRef.close(formData);
  }

  onAccept(): void {
    if (this.showGuestForm()) {
      // For guests, trigger form submission
      this.guestFormComponent()?.submitForm();
    } else {
      // For regular users, just close with true
      this.dialogRef.close(true);
    }
  }

  onRefuse(): void {
    this.dialogRef.close(false);
  }

  /**
   * Opens the Terms & Conditions dialog (which includes the Privacy Policy)
   * on top of the current disclaimer dialog.
   * @param $event The originating mouse or keyboard event, prevented to avoid
   * triggering any parent handlers.
   */
  openPrivacyPolicy($event: MouseEvent | KeyboardEvent): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.dialog.open(TermsConditionsDialog);
  }
}
